/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DocumentStorage, DocumentStorageProxyFactory, DocumentStorageService } from 'src/customText/core/utils/documentStorage/types';

const cacheQueue: string[] = [];

/**
 * Factory function which creates cache with least
 * recently used evacuation technique.
 *
 * @param connector The document storage service.
 * @param storage The storage object to cache the documents.
 * @param cacheLimit The max length of the cached items.
 */
export const getLruCachedDocumentStorage: DocumentStorageProxyFactory = (
    connector: DocumentStorageService,
    storage: DocumentStorage,
    cacheLimit: number = 50
): DocumentStorageService => {
    /**
     * Inserts element in the front of the cacheQueue and remove this element from the queue if exists.
     */
    const insertHead = (documentId: string) => {
        const elementIndex = cacheQueue.indexOf(documentId);

        if (elementIndex !== -1) {
            // Removes from the queue
            cacheQueue.splice(elementIndex, 1);
        } else if (cacheQueue.length === cacheLimit) {
            const poppedElement = cacheQueue.pop();
            storage.removeDocument(poppedElement);
        }

        // Adds to the head of the queue
        cacheQueue.splice(0, 0, documentId);
    };

    /**
     * Gets the document with the given id from
     * the cache if exists.
     */
    const getDocument = async (documentId: string, skipCache?: boolean): Promise<string> => {
        if (!skipCache && storage.hasDocument(documentId)) {
            insertHead(documentId);

            return storage.getDocument(documentId);
        }
        const documentContent = await connector.getDocument(documentId);
        try {
            storage.setDocument(documentId, documentContent);
        } catch {
            // Fail silently in case of out of memory exception
        }
        insertHead(documentId);

        return documentContent;
    };

    /**
     * Creates new document with the given content
     * and returns its new id.
     */
    const createDocument = async (content: string): Promise<string> => {
        const documentId = await connector.createDocument(content);
        try {
            storage.setDocument(documentId, content);
            insertHead(documentId);
        } catch {
            // Fail silently in case of out of memory exception
        }

        return documentId;
    };

    /**
     * Delete the document with the given id from the cache.
     */
    const deleteDocument = async (documentId: string): Promise<void> => {
        await connector.deleteDocument(documentId);

        if (storage.hasDocument(documentId)) {
            storage.removeDocument(documentId);
            // Removes item with the given key from the cache queue.
            const elementIndex = cacheQueue.indexOf(documentId);
            cacheQueue.splice(elementIndex, 1);
        }
    };

    /**
     * Updates the document of the given id with
     * the given content.
     */
    const updateDocument = async (documentId: string, content: string): Promise<void> => {
        await connector.updateDocument(documentId, content);
        try {
            storage.setDocument(documentId, content);
        } catch {
            // Fail silently in case of out of memory exception
        }
    };

    return { getDocument, createDocument, deleteDocument, updateDocument };
};
